/**
 * Chute libre — la masse change-t-elle la durée d'une chute ?
 *
 * C'est la seconde expérience, et c'est elle qui met la généricité à l'épreuve.
 * Elle ne partage avec Ératosthène ni son état, ni ses réglages, ni son
 * observable, ni son axe des abscisses, ni sa vue 3D — elle n'en a pas. Si la
 * page a réellement cessé d'être Ératosthène, ce fichier doit suffire à
 * l'ajouter. Il n'y a effectivement pas une ligne de rendu ici : que des
 * données et trois fonctions de l'état vers ce qui s'affiche.
 *
 * L'expérience elle-même tient dans un geste : **faites glisser la masse**. La
 * courbe de Galilée ne bouge pas, celle d'Aristote se déplace. Aucun réglage de
 * la vitesse de référence ne peut les faire coïncider sur deux masses à la
 * fois, et c'est ça qu'on regarde — pas l'accord obtenu sur un point.
 */

import contentJson from "../content/fall.json?raw";
import type { CelestialBody, FallModel, FallSetup } from "../core/fall.js";
import type { Quantity } from "../core/units.js";
import type {
  ChartData,
  ComparisonSection,
  Experiment,
  Preset,
  ReadoutRow,
  Series,
} from "./types.js";
import type { FallSceneRequest } from "../view/fall-scene.js";
import {
  ARISTOTLE_FALL,
  BODIES,
  GALILEO_FALL,
  fallHeightAt,
  fallTime,
  fallTrajectory,
  impactSpeed,
} from "../core/fall.js";
import { mountFallScene, renderFallShell } from "../view/fall-view.js";
import { dimensionless, quantity, valueIn } from "../core/units.js";
import { loadExperimentContent } from "../content/schema.js";
import { control } from "./types.js";
import { freeHypothesesLabel } from "../view/readout.js";
import { formatNumber, unitLabel } from "../view/format.js";
import { fr } from "../i18n/fr.js";

// ── L'état réglable ──────────────────────────────────────────────────────────

export interface FallExploration {
  readonly height: Quantity<"length">;
  readonly mass: Quantity<"mass">;
  readonly gravity: Quantity<"acceleration">;
  /** L'hypothèse libre d'Aristote : aucune théorie ne la fixe. */
  readonly referenceSpeed: Quantity<"velocity">;
}

export const DEFAULT_FALL: FallExploration = {
  height: quantity(10, "m"),
  mass: quantity(1, "kg"),
  gravity: quantity(9.80665, "m/s2"),
  referenceSpeed: quantity(5, "m/s"),
};

function setupOf(exploration: FallExploration): FallSetup {
  return {
    height: exploration.height,
    mass: exploration.mass,
    gravity: exploration.gravity,
    referenceSpeed: exploration.referenceSpeed,
  };
}

const MODELS: readonly FallModel[] = [GALILEO_FALL, ARISTOTLE_FALL];

// ── Les réglages ─────────────────────────────────────────────────────────────

/**
 * Les bornes sont des bornes de réglage, pas des affirmations sur le monde. La
 * masse va de cent grammes à cent kilos : c'est le curseur qui porte
 * l'expérience, et il faut qu'on puisse le pousser loin.
 */
export const FALL_CONTROLS = [
  control<FallExploration, "length">({
    id: "fallHeight",
    label: fr.controlFallHeight,
    hint: fr.controlFallHeightHint,
    unit: "m",
    min: 1,
    max: 200,
    step: 0.5,
    decimals: 1,
    freeHypothesis: false,
    read: (e) => e.height,
    write: (e, value) => ({ ...e, height: value }),
  }),
  control<FallExploration, "mass">({
    id: "fallMass",
    label: fr.controlFallMass,
    hint: fr.controlFallMassHint,
    unit: "kg",
    min: 0.1,
    max: 100,
    step: 0.1,
    decimals: 1,
    freeHypothesis: false,
    read: (e) => e.mass,
    write: (e, value) => ({ ...e, mass: value }),
  }),
  control<FallExploration, "acceleration">({
    id: "fallGravity",
    label: fr.controlFallGravity,
    hint: fr.controlFallGravityHint,
    unit: "m/s2",
    min: 0.5,
    max: 30,
    step: 0.005,
    decimals: 3,
    freeHypothesis: false,
    read: (e) => e.gravity,
    write: (e, value) => ({ ...e, gravity: value }),
  }),
  control<FallExploration, "velocity">({
    id: "referenceSpeed",
    label: fr.controlReferenceSpeed,
    hint: fr.controlReferenceSpeedHint,
    unit: "m/s",
    min: 0.5,
    max: 50,
    step: 0.1,
    decimals: 1,
    freeHypothesis: true,
    read: (e) => e.referenceSpeed,
    write: (e, value) => ({ ...e, referenceSpeed: value }),
  }),
];

// ── Les corps célestes, en raccourcis ────────────────────────────────────────

/**
 * Les pesanteurs publiées servent de repères, pas de bornes : le curseur va
 * au-delà, et une pesanteur qui n'existe nulle part reste un réglage licite.
 * On explore ce qu'on veut ; ce sont les mesures qui arbitrent, pas le
 * catalogue.
 */
const bodies: readonly Preset<FallExploration>[] = BODIES.map((body: CelestialBody) => ({
  id: body.id,
  label: body.label,
  apply: (exploration: FallExploration) => ({ ...exploration, gravity: body.surfaceGravity }),
}));

// ── Ce qui se trace ──────────────────────────────────────────────────────────

/** Assez de points pour que la parabole se lise comme une parabole. */
const SAMPLES = dimensionless(80);

export function fallChart(exploration: FallExploration): ChartData {
  const setup = setupOf(exploration);

  const series: readonly Series[] = MODELS.map((model) => ({
    id: model.id,
    label: model.label,
    points: fallTrajectory(model, setup, SAMPLES).map((point) => ({
      x: valueIn(point.time, "s"),
      y: valueIn(point.height, "m"),
    })),
  }));

  return {
    title: fr.fallChartTitle,
    description: fr.fallChartDescription(formatNumber(valueIn(exploration.mass, "kg"), 1)),
    caveat: fr.predictionsOnlyCaveat,
    yAxisLabel: fr.axisFallHeight,
    formatX: (seconds) => `${formatNumber(seconds, 1)} s`,
    series,
    emptyTitle: fr.noFallTitle,
    emptyExplanation: fr.noFallExplanation,
  };
}

// ── Ce qui se chiffre ────────────────────────────────────────────────────────

function readoutRow(model: FallModel, exploration: FallExploration): ReadoutRow {
  const setup = setupOf(exploration);
  const free = freeHypothesesLabel(model.freeHypotheses);
  const prediction = fallTime(model, setup);

  if (prediction.kind !== "duration") {
    return { header: model.label, cells: [prediction.reason, "—", free] };
  }

  const speed = impactSpeed(model, setup);

  return {
    header: model.label,
    cells: [
      `${formatNumber(valueIn(prediction.time, "s"), 3)} s`,
      speed === undefined ? "—" : `${formatNumber(valueIn(speed, "m/s"), 2)} ${unitLabel("m/s")}`,
      free,
    ],
  };
}

// ── La vue animée ────────────────────────────────────────────────────────────

/**
 * Noms courts pour la scène. Le libellé complet d'un modèle porte sa thèse —
 * « Galilée — tous les corps tombent de la même façon » — ce qui est juste
 * dans une table et illisible sous une bille.
 */
const SHORT_LABELS: Readonly<Record<string, string>> = {
  [GALILEO_FALL.id]: fr.galileoShort,
  [ARISTOTLE_FALL.id]: fr.aristotleShort,
};

function shortLabel(model: FallModel): string {
  return SHORT_LABELS[model.id] ?? model.label;
}

/**
 * Durée à animer : la plus longue des chutes en présence.
 *
 * S'arrêter à la plus courte couperait la scène juste avant que le second
 * corps ne touche — c'est-à-dire juste avant le moment que l'expérience existe
 * pour montrer.
 */
/**
 * La durée que la vue animée doit couvrir.
 *
 * **Exportée pour être testée**, comme `fallSceneAt` juste en dessous. Ce sont
 * les deux fonctions pures dont dépend la vue animée, et CLAUDE.md §5 fait de
 * leur pureté une règle : l'horloge choisit *quand* regarder, elle ne calcule
 * pas. Une règle qu'aucun test n'emprunte n'est pas tenue.
 */
export function longestFall(exploration: FallExploration): number {
  const setup = setupOf(exploration);
  let longest = 0;
  for (const model of MODELS) {
    const prediction = fallTime(model, setup);
    if (prediction.kind === "duration") {
      longest = Math.max(longest, valueIn(prediction.time, "s"));
    }
  }
  // Aucune durée : la scène n'a rien à animer, mais le curseur de temps doit
  // rester utilisable plutôt que de se refermer sur zéro.
  return longest > 0 ? longest : 1;
}

/** L'état de la scène à cet instant — fonction pure de l'instant (CLAUDE.md §5). */
export function fallSceneAt(
  exploration: FallExploration,
  elapsed: number,
): FallSceneRequest {
  const setup = setupOf(exploration);
  return {
    dropHeight: valueIn(exploration.height, "m"),
    bodies: MODELS.map((model) => {
      const height = fallHeightAt(model, setup, quantity(elapsed, "s"));
      return {
        id: model.id,
        label: shortLabel(model),
        height: height === undefined ? undefined : valueIn(height, "m"),
      };
    }),
  };
}

// ── Ce qui se confronte : rien, et il faut le dire ───────────────────────────

/**
 * Aucun relevé n'existe pour cette expérience.
 *
 * Il aurait été plus simple de ne pas afficher de section de confrontation du
 * tout — la page aurait paru complète. C'est exactement le glissement que
 * SPEC §10 nomme comme le seul risque qui compte : une absence ne se lit pas,
 * un refus se lit. La section reste donc là, et elle dit pourquoi elle ne peut
 * rien faire.
 */
function fallComparison(): ComparisonSection {
  return { kind: "unavailable", reasons: [fr.noDatasetForExperiment] };
}

// ── L'expérience ─────────────────────────────────────────────────────────────

export const FALL: Experiment<FallExploration> = {
  id: "chute-libre",
  label: fr.fallLabel,
  question: fr.fallQuestion,
  lede: fr.fallLede,
  content: loadExperimentContent(contentJson),

  initial: DEFAULT_FALL,
  controls: FALL_CONTROLS,
  presets: bodies,
  presetsLabel: fr.bodiesLabel,

  situationTitle: fr.sectionFallSituation,
  situationCaveat: fr.fallSituationCaveat,
  hypothesisTitle: fr.sectionAristotleControls,
  hypothesisCaveat: fr.aristotleControlsCaveat,

  chart: fallChart,
  readout: (exploration) => ({
    caption: fr.fallReadoutCaption,
    columns: [fr.columnModel, fr.columnFallTime, fr.columnImpactSpeed, fr.columnFreeHypotheses],
    rows: MODELS.map((model) => readoutRow(model, exploration)),
  }),
  comparison: fallComparison,
  scene: {
    title: fr.sectionFallScene,
    shell: () =>
      renderFallShell(MODELS.map((model) => ({ id: model.id, label: shortLabel(model) }))),
    mount: (root, current) =>
      mountFallScene(root, {
        duration: () => longestFall(current()),
        at: (elapsed) => fallSceneAt(current(), elapsed),
      }),
  },
};
